// src/ui/TaskBarTile.ts
import Phaser from 'phaser';
import { COLORS, DEPTHS, FONTS, TASKBAR, toHexColor } from '../config/gameConfig';
import type { BadgePayload, EventBus, WindowId } from '../systems/EventBus';

const ICON_BY_WINDOW: Record<WindowId, string> = {
  inbox: 'inbox',
  slack: 'message-circle',
  spreadsheet: 'table',
  'field-guide': 'book-open',
  payments: 'circle-dollar-sign',
};

export class TaskBarTile extends Phaser.GameObjects.Container {
  private readonly bus: EventBus;
  private readonly windowId: WindowId;
  private readonly bg: Phaser.GameObjects.Rectangle;
  private readonly icon: Phaser.GameObjects.Image;
  private readonly labelText: Phaser.GameObjects.Text;
  private readonly badgeCircle: Phaser.GameObjects.Arc;
  private readonly badgeText: Phaser.GameObjects.Text;
  private readonly pulseRing: Phaser.GameObjects.Arc;
  private pulseTween: Phaser.Tweens.Tween | null = null;
  private removing = false;
  private minimized = false;
  private readonly activeIndicator: Phaser.GameObjects.Rectangle;

  constructor(scene: Phaser.Scene, bus: EventBus, windowId: WindowId, badge: BadgePayload) {
    super(scene, 0, 0);
    scene.add.existing(this);
    this.setDepth(DEPTHS.taskbar + 1);
    this.bus = bus;
    this.windowId = windowId;

    this.bg = scene.add.rectangle(0, 0, TASKBAR.tileWidth, TASKBAR.tileHeight, COLORS.taskbarTileBg)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    this.bg.on('pointerover', () => this.bg.setFillStyle(COLORS.taskbarTileHover));
    this.bg.on('pointerout', () => this.bg.setFillStyle(this.minimized ? COLORS.taskbarTileBg : COLORS.taskbarTileBg));
    this.bg.on('pointerdown', () => {
      if (this.minimized) {
        this.bus.emit('window:restore-requested', { windowId: this.windowId });
      } else {
        this.bus.emit('window:focus-requested', { windowId: this.windowId });
      }
    });
    this.add(this.bg);

    // Active indicator — thin bar at the bottom; visible only when window is open.
    this.activeIndicator = scene.add
      .rectangle(TASKBAR.tileWidth / 2, TASKBAR.tileHeight - 1, 16, 2, COLORS.markedTermUnderline, 1)
      .setOrigin(0.5, 1);
    this.add(this.activeIndicator);

    this.icon = scene.add.image(
      TASKBAR.tileIconPaddingX + TASKBAR.tileIconSize / 2,
      TASKBAR.tileHeight / 2,
      `icon:${ICON_BY_WINDOW[windowId]}`,
    )
      .setDisplaySize(TASKBAR.tileIconSize, TASKBAR.tileIconSize)
      .setOrigin(0.5, 0.5)
      .setTint(badge.iconColor);
    this.add(this.icon);

    this.labelText = scene.add.text(
      TASKBAR.tileLabelPaddingX,
      TASKBAR.tileHeight / 2,
      badge.label,
      {
        fontFamily: FONTS.ui,
        fontSize: TASKBAR.tileLabelFontSize,
        color: toHexColor(COLORS.taskbarTileText),
      },
    ).setOrigin(0, 0.5);
    this.add(this.labelText);

    const badgeX = TASKBAR.tileWidth - TASKBAR.badgePaddingRight;
    const badgeY = TASKBAR.tileHeight / 2;

    this.pulseRing = scene.add.circle(badgeX, badgeY, TASKBAR.pulseRingRadius, 0, 0)
      .setStrokeStyle(TASKBAR.pulseRingBaseWidth, COLORS.taskbarPulse, 1)
      .setVisible(false);
    this.add(this.pulseRing);

    this.badgeCircle = scene.add.circle(badgeX, badgeY, TASKBAR.badgeRadius, COLORS.taskbarBadgeBg);
    this.badgeText = scene.add.text(badgeX, badgeY, '', {
      fontFamily: FONTS.ui,
      fontSize: TASKBAR.badgeFontSize,
      color: toHexColor(COLORS.taskbarBadgeText),
      fontStyle: 'bold',
    }).setOrigin(0.5, 0.5);
    this.add(this.badgeCircle);
    this.add(this.badgeText);

    this.applyBadge(badge);
    this.setAlpha(0);
    scene.tweens.add({ targets: this, alpha: 1, duration: TASKBAR.windowFadeMs });
  }

  applyBadge(badge: BadgePayload): void {
    this.labelText.setText(badge.label);
    this.icon.setTint(badge.iconColor);
    const showBubble = badge.count > 0;
    this.badgeCircle.setVisible(showBubble);
    this.badgeText.setVisible(showBubble);
    if (showBubble) this.badgeText.setText(String(badge.count));
    // Pulse only fires when window is minimized — open windows never pulse.
    this.setPulse(badge.pulse && this.minimized);
  }

  setMinimized(minimized: boolean): void {
    if (this.minimized === minimized) return;
    this.minimized = minimized;
    this.activeIndicator.setVisible(!minimized);
    // If we just became open, kill any in-flight pulse.
    if (!minimized) this.setPulse(false);
    // Fade icon/label slightly when minimized to signal "hidden" state.
    const dim = minimized ? 0.7 : 1;
    this.icon.setAlpha(dim);
    this.labelText.setAlpha(dim);
  }

  isMinimizedState(): boolean {
    return this.minimized;
  }

  private setPulse(on: boolean): void {
    if (on && !this.pulseTween) {
      this.pulseRing.setVisible(true);
      this.pulseRing.setScale(1);
      this.pulseTween = this.scene.tweens.add({
        targets: this.pulseRing,
        scale: TASKBAR.pulseRingMaxScale,
        alpha: { from: 1, to: 0 },
        duration: TASKBAR.pulseLoopMs,
        repeat: -1,
        ease: 'Sine.easeOut',
        onRepeat: () => {
          this.pulseRing.setAlpha(1);
          this.pulseRing.setScale(1);
        },
      });
    } else if (!on && this.pulseTween) {
      this.pulseTween.remove();
      this.pulseTween = null;
      this.pulseRing.setVisible(false);
      this.pulseRing.setAlpha(1);
      this.pulseRing.setScale(1);
    }
  }

  removeWithFade(onComplete: () => void): void {
    if (this.removing) return;
    this.removing = true;
    if (this.pulseTween) {
      this.pulseTween.remove();
      this.pulseTween = null;
    }
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: TASKBAR.windowFadeMs,
      onComplete: () => {
        this.destroy();
        onComplete();
      },
    });
  }
}
